// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useReactOidc } from '@axa-fr/react-oidc-context'
import { useTranslation, Trans } from 'react-i18next'
import { Box, FormField } from 'grommet'
import { debounce, isEmpty, uniqBy } from 'lodash'

import { isTenant } from '../../../utils/feature-flag-utils'
import {
  Button,
  CCSForm,
  Loader,
  ModalDialog,
  ModalHeader,
  ModalFooter,
  Notification,
  MultiSelectBox,
  Typography
} from '../../../components'
import {
  get,
  getBaseUrl,
  getErrorMessage,
  patch
} from '../../../utils/api-utils'
import { GROUP_SCHEMAS } from '../groups/constants'
import { updateV2RBACPolicies } from '../../../utils/rbac-api-utils'
import { useCCSContext } from '../../../context/ccs-context'
import { useVisibilityContext } from '../../../context/visibility-context'
import { getAuthzSAMLClaimsUrl } from '../../manage-account/utils'
import { isExternalUser } from '../../manage-account/identity/utils'

const AssignUsersToGroupModal = ({
  onSetOpen,
  onSubmit,
  setNotificationInfo = () => {},
  group
}) => {
  const { csrfToken } = useCCSContext()
  const { dispatchVisibilityContext } = useVisibilityContext()
  const { oidcUser } = useReactOidc()
  const { t } = useTranslation(['common', 'iam'])
  const [users, setUsers] = useState([])
  const [selectedUsers, setSelectedUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [samlLoading, setSAMLLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [samlAuthzUsers, setSAMLAuthzUsers] = useState([])
  const [dropdownOpened, setDropdownOpened] = useState(false)
  const usersPerScroll = 200
  const [totalUsers, setTotalUsers] = useState(usersPerScroll)
  const [scroll, setScroll] = useState(1)

  const closeModal = () => {
    onSetOpen(false)
  }

  const validateSelectUsers = () => {
    if (selectedUsers?.length === 0) {
      setErrorMessage(t('common:this_is_required'))
      return false
    }
    return true
  }

  const template = (option) => (
    <Box pad={{ top: 'xsmall' }}>
      <Typography size="small" type="paragraph">
        {option?.displayName || option?.userName}
      </Typography>
      {option?.displayName && (
        <Typography size="xsmall" type="paragraph">
          {option?.userName}
        </Typography>
      )}
    </Box>
  )

  const samlAuthzWarning = (
    <Box width={{ min: 'full', max: 'medium' }}>
      {samlLoading ? (
        <Loader testId="check-saml-authz-users-loader" />
      ) : (
        <Notification
          testId="saml-authz-selected-warning"
          type="inline"
          status="warning"
          text={t('iam:ccs_attribute.add_saml_users_to_group_warning', {
            numSAMLAuthzUsers: samlAuthzUsers?.length || 0,
            totalSelectedUsers: selectedUsers?.length,
            users: t('common:business_object.user_plural'),
            domains: t('common:business_object.domain'),
            userGroups: t('common:business_object.user_group_plural'),
            roleAssignments: t('common:business_object.role_assignment_plural')
          })}
        />
      )}
    </Box>
  )

  const getUsersAssignedToGroup = async () => {
    const isMounted = true
    let usersAssignedToGroup
    await get(
      `/identity/v2alpha1/scim/v2/extensions/Groups/${group?.id}/users`
    ).then(
      (response) => {
        if (!isMounted) return
        usersAssignedToGroup = response?.data?.Resources
      },
      (error) => {
        if (!isMounted) return
        setNotificationInfo(getErrorMessage(error, t), 'error')
      }
    )
    return usersAssignedToGroup
  }

  // when dropdown is closed previously selected user is not part of the dropdown
  // add it to the dropdown (similar approach followed at other places where dropdown with pagination used)
  const checkAndAddSelectedUser = (userList) => {
    selectedUsers.forEach((selectedUser) => {
      if (
        !userList.find(
          (userItem) => userItem.userName === selectedUser.userName
        )
      ) {
        userList.push({
          ...selectedUser
        })
      }
    })
  }

  const handleDebouncedSearchValue = debounce((value) => {
    setUsers([])
    setSearchValue(value)
    setScroll(1)
  }, 500)

  const getUsers = () => {
    const searchValueTrimmed = searchValue?.trim()

    const searchQuery =
      searchValueTrimmed?.length > 0
        ? `filter=${encodeURIComponent(
            `displayName sw "${searchValueTrimmed}" or userName sw "${searchValueTrimmed}"`
          )}&`
        : ''

    setLoading(true)

    return get(
      `/identity/v2alpha1/scim/v2/Users?${searchQuery}count=${usersPerScroll}&startIndex=${
        (scroll - 1) * usersPerScroll + 1
      }`
    )
      .then(async (response) => {
        setTotalUsers(response?.data?.totalResults || usersPerScroll)
        let usersToDisplay = []
        if (response?.data && response?.data?.Resources) {
          usersToDisplay = response?.data?.Resources
        }
        // filtering external user for msp customers. This code is taken from ui-p2p4 branch
        if (isTenant()) {
          usersToDisplay = usersToDisplay.filter((user) => {
            return !isExternalUser(user)
          })
        }
        if (group?.id) {
          const usersAssignedToGroup = await getUsersAssignedToGroup()
          const usersAssignedToGroupIds = usersAssignedToGroup?.map(
            (user) => user?.id
          )
          usersToDisplay = usersToDisplay?.filter(
            (user) => !usersAssignedToGroupIds?.includes(user?.id)
          )
        }
        let userData
        if (scroll !== 1) {
          userData = [...users, ...usersToDisplay]
        } else {
          userData = usersToDisplay
        }
        checkAndAddSelectedUser(userData)

        setUsers(uniqBy(userData, 'userName'))
      })
      .catch((error) => {
        setNotificationInfo(getErrorMessage(error, t), 'error')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    getUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scroll, searchValue])

  const onSelectionChanged = (newSelectedUsers) => {
    // filter for new selections that not in current selected users
    // since we only need to send GET API to the new selection(s)
    const urls = newSelectedUsers
      ?.filter(
        (newSelectedUser) =>
          !selectedUsers
            ?.map((currentSelectedUser) => currentSelectedUser?.id)
            ?.includes(newSelectedUser?.id)
      )
      ?.map((user) => {
        return {
          id: user?.id,
          url: getAuthzSAMLClaimsUrl(user?.userName)
        }
      })

    // filter for SAML authz entry that in the new selection
    // since the previous SAML authz entries might be removed from the selections
    const newSAMLAuthzUsers = samlAuthzUsers?.filter((samlAuthzUser) =>
      newSelectedUsers
        ?.map((newSelectedUser) => newSelectedUser?.id)
        .includes(samlAuthzUser)
    )
    setSAMLLoading(true)
    setSelectedUsers(newSelectedUsers)
    const promises = urls?.map((url) =>
      get(url?.url, {}, oidcUser.access_token)
    )
    if (!isEmpty(promises)) {
      // send all GET SAML requests concurrently
      Promise.all(promises)
        .then((results) => {
          if (results && results?.length === promises?.length) {
            results?.forEach((res, i) => {
              if (res?.data?.sso_mode) {
                newSAMLAuthzUsers.push(urls[i]?.id)
              }
            })
          }
          setSAMLAuthzUsers(newSAMLAuthzUsers)
          setSAMLLoading(false)
        })
        .catch((error) => {
          setSAMLLoading(false)
          setNotificationInfo(getErrorMessage(error, t), 'error')
        })
    } else {
      setSAMLLoading(false)
      setSAMLAuthzUsers(newSAMLAuthzUsers)
    }
  }

  const updateGroupUsers = async () => {
    setLoading(true)
    const nonSAMLAuthzSelectedUsers =
      samlAuthzUsers?.length > 0
        ? selectedUsers?.filter((s) => !samlAuthzUsers?.includes(s?.id))
        : selectedUsers
    const value = nonSAMLAuthzSelectedUsers?.map((u) => ({
      display: u?.displayName,
      $ref: `${getBaseUrl()}/identity/v2alpha1/scim/v2/Users/${u?.id}`,
      value: u?.id
    }))
    if (nonSAMLAuthzSelectedUsers?.length > 0) {
      try {
        await patch(`/identity/v2alpha1/scim/v2/Groups/${group?.id}`, {
          schemas: [GROUP_SCHEMAS.PATCH_OP_SCHEMA],
          Operations: [
            {
              op: 'add',
              value,
              path: 'members'
            }
          ]
        })
        setNotificationInfo(
          nonSAMLAuthzSelectedUsers?.length === 1 ? (
            <Typography type="text">
              <Trans
                i18nKey="iam:organization_groups.user_added_message_v2"
                t={t}
                values={{
                  userName:
                    nonSAMLAuthzSelectedUsers[0]?.displayName ||
                    nonSAMLAuthzSelectedUsers[0]?.userName,
                  groupName: group?.displayName
                }}
              />
            </Typography>
          ) : (
            <Typography type="text">
              <Trans
                i18nKey="iam:organization_groups.users_added_message_v2"
                t={t}
                values={{
                  usercount: nonSAMLAuthzSelectedUsers?.length,
                  groupName: group?.displayName
                }}
              />
            </Typography>
          ),
          'info',
          nonSAMLAuthzSelectedUsers?.length === 1
            ? t('iam:organization_groups.user_added_title')
            : t('iam:organization_groups.users_added_title')
        )
        // This could affect the current user's permissions hence why we're calling updateV2RBACPolicies
        // updateV2RBACPolicies will not run on local as csrfToken will be null
        updateV2RBACPolicies(dispatchVisibilityContext, csrfToken)
        onSubmit()
      } catch (error) {
        setNotificationInfo(getErrorMessage(error, t), 'error')
      } finally {
        setLoading(false)
        closeModal()
      }
    } else {
      closeModal()
    }
  }

  return (
    <ModalDialog
      height="100%"
      overflow="hidden"
      position="right"
      header={
        <ModalHeader
          title={
            <Box direction="column">
              <Typography
                type="heading"
                level="2"
                margin={{ bottom: 'small' }}
                emphasis
              >
                {t('iam:organization_groups.add_users_to_group_title')}
              </Typography>
              <Box width="medium">
                <Typography type="text">
                  <Trans
                    i18nKey="iam:organization_groups.add_users_to_group_desc_v2"
                    t={t}
                    values={{ groupName: group?.displayName }}
                  />
                </Typography>
              </Box>
            </Box>
          }
          onClose={closeModal}
        />
      }
      content={
        <Box margin={{ top: 'medium' }}>
          <Box
            justify={dropdownOpened ? 'between' : 'start'}
            gap="small"
            height="large"
          >
            <CCSForm errorMessage="" testId="assign-users-to-group-form">
              <FormField
                label={t('iam:users.select_user_label')}
                required
                error={errorMessage}
              >
                <MultiSelectBox
                  options={users}
                  labelKey={(user) =>
                    isEmpty(user?.displayName)
                      ? user?.userName
                      : user?.displayName
                  }
                  valueKey="id"
                  placeholder={t('common:select')}
                  onChange={(user) => {
                    onSelectionChanged(user)
                    setErrorMessage('')
                  }}
                  onClose={() => {
                    if (searchValue) {
                      setScroll(1)
                      setSearchValue('')
                    }
                  }}
                  onMore={() => {
                    if (totalUsers > scroll * usersPerScroll) {
                      setScroll(scroll + 1)
                    }
                  }}
                  onOpen={() => setDropdownOpened(true)}
                  onSearch={(searchText) => {
                    handleDebouncedSearchValue(searchText)
                  }}
                  searchPlaceholder={t('common:search')}
                  emptySearchMessage={
                    loading
                      ? t('common:searching')
                      : t('common:search_no_matches')
                  }
                  dropHeight="medium"
                  disabled={loading}
                  testId="assign-users-to-group-multiselect"
                >
                  {template}
                </MultiSelectBox>
              </FormField>
            </CCSForm>
            {samlAuthzUsers?.length > 0 && samlAuthzWarning}
          </Box>
          <Box align="center" margin={{ top: 'small' }}>
            {loading && <Loader testId="assign-users-to-group-loader" />}
          </Box>
        </Box>
      }
      footer={
        <ModalFooter
          left={
            <Box direction="row" gap="small">
              <Button
                primary
                label={t('common:add')}
                onClick={() => {
                  if (validateSelectUsers()) {
                    updateGroupUsers()
                  }
                }}
                disabled={loading}
                testId="assign-users-to-group-add-btn"
              />
              <Button
                default
                label={t('common:cancel')}
                onClick={closeModal}
                testId="assign-users-to-group-cancel-btn"
              />
            </Box>
          }
        />
      }
      onClose={closeModal}
      onClickOutside={closeModal}
      testId="assign-users-to-group-modal"
    />
  )
}

AssignUsersToGroupModal.propTypes = {
  /**
   * Flag to display modal
   */
  onSetOpen: PropTypes.func.isRequired,

  /**
   * onSubmit handler
   */
  onSubmit: PropTypes.func.isRequired,

  /**
   * Set notification info
   */
  setNotificationInfo: PropTypes.func,

  /**
   * The group to add users
   */
  group: PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired
  }).isRequired
}

export default AssignUsersToGroupModal
